import { authFetch } from '../../services/authClient';
import { ChatResponse, ResearchTurnStatus } from '../../types/deep-research';

/**
 * SSE client for `POST /research/stream`. The backend emits line-buffered
 * `data: {…}` frames with `type: meta | status | token | result | done | error`,
 * mirroring enviro-chat's classify_route/stream convention. Report text arrives
 * as raw markdown chunks that must be concatenated before rendering.
 *
 * A dropped connection does not kill the research run — the server finishes and
 * writes the result to Firestore, so callers fall back to polling
 * `/research/get_pred` with the same chatId (see pollResearchResult).
 */

export type ResearchEvent =
  | { type: 'meta'; chat_id: string | number; research_status: ResearchTurnStatus; task_type?: string }
  | { type: 'status'; message: string }
  | { type: 'token'; content: string }
  | { type: 'result'; chat_id: string | number; status: ResearchTurnStatus; pdf?: string | null }
  | { type: 'done' }
  | { type: 'error'; message: string };

export interface ResearchStreamHandlers {
  onMeta?: (chatId: string | number | null, researchStatus: ResearchTurnStatus | null) => void;
  onStatus?: (message: string) => void;
  onToken: (content: string) => void;
  onResult?: (result: { chatId: string | number | null; status: ResearchTurnStatus | null; pdf: string | null }) => void;
  onError?: (message: string) => void;
  onDone?: () => void;
}

const streamUrl = () => `${process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT}/research/stream`;
const predUrl = () => `${process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT}/research/get_pred`;

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new Error('aborted'));
    };
    if (signal?.aborted) { onAbort(); return; }
    signal?.addEventListener('abort', onAbort, { once: true });
  });

/**
 * Reconnect path for a stream that dropped mid-run. Polls `/research/get_pred`
 * with the same chatId until the run leaves `executing`, then returns that
 * response. Its `content` is HTML, not markdown — tag the message accordingly.
 * Returns null if the run is still executing when the timeout is reached.
 */
export const pollResearchResult = async (
  chatId: string | number,
  userId: string | null,
  signal?: AbortSignal,
): Promise<ChatResponse | null> => {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await wait(POLL_INTERVAL_MS, signal);

    const params = new URLSearchParams({
      chatStatus: 'ongoing',
      q: encodeURIComponent('keep alive'),
      chatId: String(chatId),
    });
    if (userId) { params.append('userId', encodeURIComponent(userId)); }

    const res = await authFetch(`${predUrl()}?${params.toString()}`, { method: 'GET', signal });
    if (!res.ok) { continue; }
    const data = (await res.json()) as ChatResponse | null;
    if (data && data.status !== 'executing') { return data; }
  }

  return null;
};

/**
 * Opens the stream and dispatches events until the server closes it.
 * Resolves with whether a terminal `result` event was seen — `false` means the
 * connection dropped mid-run and the caller should fall back to polling.
 */
export const streamResearch = async (
  formData: FormData,
  handlers: ResearchStreamHandlers,
  signal?: AbortSignal,
): Promise<boolean> => {
  const res = await authFetch(streamUrl(), { method: 'POST', body: formData, signal });
  if (!res.ok) {
    // Failures before the stream opens (400 / 401 / 413) come back as plain JSON.
    if (res.status === 413) { throw new Error('FILE_TOO_LARGE'); }
    const detail = await res.text().catch(() => '');
    throw new Error(`research/stream failed: ${res.status} ${detail}`);
  }
  if (!res.body) { throw new Error('No response body'); }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let carryOver = '';
  let sawResult = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) { break; }
    if (signal?.aborted) { break; }

    const chunk = carryOver + decoder.decode(value, { stream: true });
    const lines = chunk.split('\n');
    carryOver = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data:')) { continue; }
      let event: ResearchEvent;
      try {
        event = JSON.parse(line.slice(5).trim()) as ResearchEvent;
      } catch {
        continue;
      }

      if (event.type === 'status') {
        handlers.onStatus?.(event.message ?? '');
      } else if (event.type === 'meta') {
        handlers.onMeta?.(event.chat_id ?? null, event.research_status ?? null);
      } else if (event.type === 'token') {
        handlers.onToken(event.content ?? '');
      } else if (event.type === 'result') {
        sawResult = true;
        handlers.onResult?.({
          chatId: event.chat_id ?? null,
          status: event.status ?? null,
          pdf: event.pdf ?? null,
        });
      } else if (event.type === 'error') {
        handlers.onError?.(event.message ?? 'Server error');
      } else if (event.type === 'done') {
        handlers.onDone?.();
      }
    }
  }

  return sawResult;
};
